import { useState, type ReactNode } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTriggerWithHover } from "@shared-kernel/primary/shared/shadcn/ui/collapsible";
import { ChevronRight } from "lucide-react";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import type { ActivityCardEntry } from "./types";
import { ActivityCardAvatar, ActivityCardContent } from "./activity-card-shared";

export interface CollapsibleActivityCardProps {
  entry: ActivityCardEntry;
  /** Custom trailing content (e.g., amount display). */
  trailing?: ReactNode;
  /** Content to display when the card is expanded. */
  children: ReactNode;
}

export const CollapsibleActivityCard = ({ entry, trailing, children }: CollapsibleActivityCardProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="rounded-lg border border-border bg-card">
      <CollapsibleTriggerWithHover
        isOpen={isOpen}
        className="flex w-full cursor-pointer items-center gap-4 p-2 text-left transition-colors"
      >
        <ActivityCardAvatar entry={entry} />
        <div className="min-w-0 flex-1">
          <ActivityCardContent entry={entry} />
        </div>
        {trailing}
        <ChevronRight className={cn("size-5 text-muted-foreground transition-transform duration-200", isOpen && "rotate-90")} />
      </CollapsibleTriggerWithHover>

      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <div className="flex gap-6 px-4 py-3">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};
